import { useMemo, useState, useContext } from 'react'
import type { ActionDefinition, ActionOutcome, ActionSurface } from '../../services/actions/action'
import { invokeAction, listActions, teachAction } from '../../services/actions/registry'
import { AuthContext } from '../../context/authContextCore'
import { WorkspaceContext } from '../../context/workspaceContextCore'
import { ToastContext } from '../../context/toastContextCore'
import './DevActionsPanel.css'

// Compact generic JSON-Schema-to-form renderer (< 100 LOC)
const SchemaFormModal = ({
  action,
  onClose,
}: {
  action: ActionDefinition
  onClose: () => void
}) => {
  const authContext = useContext(AuthContext)
  const workspaceContext = useContext(WorkspaceContext)
  const toastContext = useContext(ToastContext)

  const schema = action.argsSchema as unknown as {
    properties?: Record<string, { type?: string; description?: string; default?: unknown }>
    required?: string[]
  }
  const properties = schema?.properties ?? {}
  const required = schema?.required ?? []

  const [formValues, setFormValues] = useState<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = {}
    for (const [key, prop] of Object.entries(properties)) {
      if (prop.default !== undefined) initial[key] = prop.default
      else if (prop.type === 'boolean') initial[key] = false
      else if (prop.type === 'number' || prop.type === 'integer') initial[key] = 0
      else if (prop.type === 'string') initial[key] = ''
      else if (prop.type === 'object') initial[key] = '{}'
      else if (prop.type === 'array') initial[key] = '[]'
    }
    return initial
  })

  const [invokedFrom, setInvokedFrom] = useState<ActionSurface>(action.surfaces[0] ?? 'catalog')
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null)
  const [invoking, setInvoking] = useState(false)

  const handleFieldChange = (key: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [key]: value }))
  }

  const handleInvoke = async () => {
    setInvoking(true)
    setOutcome(null)
    try {
      const parsedArgs: Record<string, unknown> = {}
      for (const [key, prop] of Object.entries(properties)) {
        const raw = formValues[key]
        if (prop.type === 'number' || prop.type === 'integer') {
          parsedArgs[key] = typeof raw === 'number' ? raw : Number(raw)
        } else if (prop.type === 'object' || prop.type === 'array') {
          try {
            parsedArgs[key] = typeof raw === 'string' ? JSON.parse(raw) : raw
          } catch {
            parsedArgs[key] = raw
          }
        } else {
          parsedArgs[key] = raw
        }
      }

      const res = await invokeAction(action.id, parsedArgs, {
        workspaceId: workspaceContext?.activeWorkspaceId ?? '',
        actor: {
          uid: authContext?.user?.uid ?? 'dev-user',
          role: authContext?.userProfile?.role ?? 'ADMIN',
          displayName: authContext?.userProfile?.displayName ?? 'Dev User',
        },
        canPerform: () => true,
        invokedFrom,
        showToast: toastContext?.showToast,
      })
      setOutcome(res)
    } catch (err: unknown) {
      setOutcome({
        kind: 'FAILED',
        message: err instanceof Error ? err.message : String(err),
        recoverable: false,
        correlationId: 'dev-error',
      })
    } finally {
      setInvoking(false)
    }
  }

  return (
    <div className="devModalOverlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="devModalContent" onClick={(e) => e.stopPropagation()}>
        <div className="devModalHeader">
          <div>
            <div className="devModalTitle">{action.label}</div>
            <div className="devActionId">{action.id}</div>
          </div>
          <button className="devModalCloseBtn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="devModalBody">
          <div className="devFormField">
            <label className="devFormLabel">Invoked From Surface</label>
            <select
              className="devActionsSelect"
              value={invokedFrom}
              onChange={(e) => setInvokedFrom(e.target.value as ActionSurface)}
            >
              {action.surfaces.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {Object.entries(properties).map(([key, prop]) => {
            const isReq = required.includes(key)
            return (
              <div key={key} className="devFormField">
                <label className="devFormLabel">
                  <span>{key}</span>
                  <span className="devChip">{prop.type || 'any'}</span>
                  {isReq && <span className="devFormRequired">*required</span>}
                </label>
                {prop.type === 'boolean' ? (
                  <label className="devFormCheckbox">
                    <input
                      type="checkbox"
                      checked={Boolean(formValues[key])}
                      onChange={(e) => handleFieldChange(key, e.target.checked)}
                    />
                    <span>{prop.description || key}</span>
                  </label>
                ) : prop.type === 'object' || prop.type === 'array' ? (
                  <textarea
                    className="devFormTextarea"
                    value={String(formValues[key] ?? '')}
                    placeholder={`JSON for ${key}`}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                  />
                ) : (
                  <input
                    type={prop.type === 'number' || prop.type === 'integer' ? 'number' : 'text'}
                    className="devFormInput"
                    value={String(formValues[key] ?? '')}
                    placeholder={prop.description || key}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                  />
                )}
              </div>
            )
          })}

          {Object.keys(properties).length === 0 && (
            <p className="devActionDescription">This action requires no arguments.</p>
          )}

          {outcome && (
            <div className="devOutcomeCard">
              <div className={`devOutcomeStatus devOutcomeStatus${outcome.kind.startsWith('BLOCKED') ? 'BLOCKED' : outcome.kind}`}>
                Outcome: {outcome.kind}
              </div>
              <pre className="devActionSchemaPre">{JSON.stringify(outcome, null, 2)}</pre>
            </div>
          )}
        </div>

        <div className="devModalFooter">
          <button className="devBtn devBtnSecondary" onClick={onClose}>Close</button>
          <button className="devBtn" onClick={handleInvoke} disabled={invoking}>
            {invoking ? 'Invoking…' : 'Run Action'}
          </button>
        </div>
      </div>
    </div>
  )
}

export const DevActionsPanel = () => {
  const [search, setSearch] = useState('')
  const [selectedDomain, setSelectedDomain] = useState<string>('all')
  const [selectedSurface, setSelectedSurface] = useState<string>('all')
  const [selectedRisk, setSelectedRisk] = useState<string>('all')
  const [expandedSchemas, setExpandedSchemas] = useState<Record<string, boolean>>({})
  const [modalAction, setModalAction] = useState<ActionDefinition | null>(null)
  const [teachStatus, setTeachStatus] = useState<{ id: string; msg: string } | null>(null)

  const allActions = useMemo(() => listActions(), [])

  const domains = useMemo(() => {
    const set = new Set<string>()
    for (const a of allActions) set.add(a.domain)
    return Array.from(set).sort()
  }, [allActions])

  const filteredActions = useMemo(() => {
    return allActions.filter((a) => {
      if (selectedDomain !== 'all' && a.domain !== selectedDomain) return false
      if (selectedSurface !== 'all' && !a.surfaces.includes(selectedSurface as ActionSurface)) return false
      if (selectedRisk !== 'all' && a.risk !== selectedRisk) return false
      if (search.trim()) {
        const q = search.toLowerCase()
        if (!a.id.toLowerCase().includes(q) && !a.label.toLowerCase().includes(q) && !a.description.toLowerCase().includes(q)) {
          return false
        }
      }
      return true
    })
  }, [allActions, selectedDomain, selectedSurface, selectedRisk, search])

  const toggleSchema = (id: string) => {
    setExpandedSchemas((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleTestTeach = async (action: ActionDefinition) => {
    const outcome = await teachAction(action.id, {
      workspaceId: 'dev-workspace',
      actor: { uid: 'dev-user' },
      canPerform: () => true,
      invokedFrom: 'catalog',
    })
    setTeachStatus({
      id: action.id,
      msg: outcome.kind === 'COMPLETED' ? `Tour: ${outcome.result.tourId}` : `Teach failed: ${outcome.message}`,
    })
  }

  return (
    <div className="devActionsRoot" data-testid="dev-actions-panel">
      <div className="devActionsHeader">
        <div className="devActionsTitleRow">
          <div className="devActionsTitle">
            <span>Action Registry Dev Explorer</span>
            <span className="devBadge">DEV ONLY</span>
          </div>
          <div className="devActionsCount">
            Showing {filteredActions.length} of {allActions.length} actions
          </div>
        </div>

        <div className="devActionsFilters">
          <input
            type="search"
            className="devActionsSearchInput"
            placeholder="Search action id, label, or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="devActionsSelect"
            value={selectedDomain}
            onChange={(e) => setSelectedDomain(e.target.value)}
          >
            <option value="all">All Domains ({domains.length})</option>
            {domains.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>

          <select
            className="devActionsSelect"
            value={selectedSurface}
            onChange={(e) => setSelectedSurface(e.target.value)}
          >
            <option value="all">All Surfaces</option>
            <option value="orbit">orbit</option>
            <option value="automate">automate</option>
            <option value="ai">ai</option>
            <option value="catalog">catalog</option>
            <option value="palette">palette</option>
            <option value="keyboard">keyboard</option>
          </select>

          <select
            className="devActionsSelect"
            value={selectedRisk}
            onChange={(e) => setSelectedRisk(e.target.value)}
          >
            <option value="all">All Risks</option>
            <option value="READ_ONLY">READ_ONLY</option>
            <option value="LOW">LOW</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="HIGH">HIGH</option>
          </select>
        </div>
      </div>

      <div className="devActionsList">
        {filteredActions.map((action) => {
          const isExpanded = expandedSchemas[action.id] ?? false
          const riskClass = action.risk === 'READ_ONLY'
            ? 'devChipRiskReadOnly'
            : action.risk === 'LOW'
            ? 'devChipRiskLow'
            : action.risk === 'MEDIUM'
            ? 'devChipRiskMedium'
            : 'devChipRiskHigh'

          return (
            <div key={action.id} className="devActionCard" data-testid={`dev-action-card-${action.id}`}>
              <div className="devActionCardHeader">
                <div>
                  <div className="devActionLabel">{action.label}</div>
                  <div className="devActionId">{action.id}</div>
                </div>
                <span className={`devChip ${riskClass}`}>{action.risk}</span>
              </div>

              <div className="devActionMetaRow">
                <span className="devChip">domain: {action.domain}</span>
                {action.surfaces.map((s) => (
                  <span key={s} className="devChip">{s}</span>
                ))}
                {action.tourId && <span className="devChip" style={{ color: '#c084fc' }}>tour: {action.tourId}</span>}
              </div>

              <div className="devActionDescription">{action.description}</div>

              <div>
                <button className="devActionSchemaToggle" onClick={() => toggleSchema(action.id)}>
                  {isExpanded ? '▼ Hide argsSchema' : '▶ View argsSchema'}
                </button>
                {isExpanded && (
                  <pre className="devActionSchemaPre">
                    {JSON.stringify(action.argsSchema, null, 2)}
                  </pre>
                )}
              </div>

              {teachStatus && teachStatus.id === action.id && (
                <div style={{ fontSize: 12, color: '#38bdf8' }}>{teachStatus.msg}</div>
              )}

              <div className="devActionFooter">
                {action.tourId && (
                  <button className="devBtn devBtnSecondary" onClick={() => handleTestTeach(action)}>
                    Test teach
                  </button>
                )}
                <button className="devBtn" onClick={() => setModalAction(action)}>
                  Test invoke
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {modalAction && (
        <SchemaFormModal
          action={modalAction}
          onClose={() => setModalAction(null)}
        />
      )}
    </div>
  )
}

export default DevActionsPanel
